import React from 'react';
import { cn } from './cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'tertiary';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-accent-gold text-bg-0 hover:brightness-110 active:brightness-95 shadow-2 hover:shadow-3 rounded-xl font-semibold',
  secondary: 'bg-accent-indigo/20 text-text-primary border border-accent-indigo/70 hover:border-accent-indigo hover:bg-accent-indigo/30 rounded-xl font-medium',
  tertiary: 'bg-transparent text-text-secondary hover:bg-white/5 hover:text-text-primary rounded-md font-medium',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 px-4 text-small',
  md: 'h-11 px-5 text-body',
  lg: 'h-14 px-7 text-body-lg',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, iconPosition = 'left', isLoading, className, children, disabled, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 transition-all duration-150 ease-out-quint disabled:opacity-50 disabled:cursor-not-allowed select-none',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || isLoading}
      {...rest}
    >
      {isLoading ? <Spinner size="sm" /> : icon && iconPosition === 'left' && <span className="shrink-0">{icon}</span>}
      <span>{children}</span>
      {!isLoading && icon && iconPosition === 'right' && <span className="shrink-0">{icon}</span>}
    </button>
  );
});
